import { Core } from "@faire/design-tokens";
import { BRANDS_CATEGORY_ID } from "@faire/help-center-utils/consts/BRANDS_CATEGORY_ID";
import { localeString } from "@faire/help-center-utils/lib/localeString";
import { getBrandToken } from "@faire/help-center-utils/serialized-data/getBrand";
import { getRetailerToken } from "@faire/help-center-utils/serialized-data/getRetailer";
import { isHiddenSection } from "@faire/help-center/consts";
import {
  NAVIGATION_FROM,
  SEARCH_SECTION_FROM,
} from "@faire/help-center/views/eventEnum";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { SafelySetInnerHTML } from "@faire/web--source/common/SafelySetInnerHTML";
import { Search } from "@faire/web--source/slate/icons/foundation/Search";
import { Spacer } from "@faire/web--source/slate/spacing";
import { useState } from "@faire/web--source/ui/hooks/useState";
import { Link } from "@faire/web--source/ui/LinkAnchor";
import searchArticles, {
  QueryParameters as ISearchArticlesRequest,
} from "@faire/web-api--source/api/zendesk/articles/search/get";
import { ISearchArticlesResponse } from "@faire/web-api--source/com/faire/vendor/zendesk/ISearchArticlesResponse";
import { IZendeskArticle } from "@faire/web-api--source/com/faire/vendor/zendesk/IZendeskArticle";
import {
  trackHelpCenterSearchArticleKeyboardInput,
  trackHelpCenterSearchArticleKeyboardInputTraceApiRequestEnd,
  trackHelpCenterSearchArticleKeyboardInputTraceApiRequestStart,
  trackHelpCenterSearchArticleKeyboardInputTraceRendered,
} from "@faire/web-api--source/events/helpCenter/keyboardInput/searchArticle";
import { trackHelpCenterSearchEnterKeyboardInput } from "@faire/web-api--source/events/helpCenter/keyboardInput/searchEnter";
import home from "@faire/web-api--source/routes/support";
import articlePage from "@faire/web-api--source/routes/support/articles/articleId";
import brands from "@faire/web-api--source/routes/support/brands";
import searchPage from "@faire/web-api--source/routes/support/search";
import get from "lodash/get";
import qs from "query-string";
import * as React from "react";
import { useHistory } from "react-router-dom";

import {
  IconWrapper,
  scrollToTopIfNeeded,
  SearchInput,
  SearchResult,
  StyledLoadingSpinner,
  Wrapper,
} from "./SearchField.styles";

interface ISearchResult {
  id: string;
  title: string;
  snippet: string;
}

interface SearchFieldProps {
  mini?: boolean;
  miniInputSize?: number;
  expanded?: boolean;
  categoryId?: number;
  isLoading?: boolean;
  autoFocus?: boolean;
  navigatedFrom?: NAVIGATION_FROM;
  sectionFrom?: SEARCH_SECTION_FROM;
}

export const SearchField: React.FC<SearchFieldProps> = ({
  mini,
  expanded,
  miniInputSize,
  isLoading,
  autoFocus,
  categoryId,
  navigatedFrom,
  sectionFrom,
}) => {
  const listRef = React.useRef<HTMLUListElement | null>(null);
  const itemRef = React.useRef<HTMLLIElement | null>(null);

  const [searchResult, setSearchResult] = useState<ISearchResult[]>([]);
  const history = useHistory();
  const [searchIsLoading, setLoading] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [showResult, setShowResult] = useState<boolean>(false);
  const [selected, setSelected] = useState<number>(-1);
  const { strictLocalize } = useStrictLocalization();

  const abortControllerRef = React.useRef<AbortController>(
    new AbortController()
  );

  const fetchSearchResults = async (
    event: React.FormEvent<HTMLInputElement>
  ) => {
    try {
      const value = get(event, "currentTarget.value");
      initSearchState(value);
      if (!value || value.length < 3) {
        setSearchResult([]);
        return;
      }
      setLoading(true);

      trackHelpCenterSearchArticleKeyboardInputTraceApiRequestStart();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      const response: ISearchArticlesResponse = await searchArticles(
        ISearchArticlesRequest.build({
          query: value,
          category: categoryId,
          locale: localeString,
        }),
        {
          signal: abortController.signal,
        }
      );

      trackHelpCenterSearchArticleKeyboardInputTraceApiRequestEnd();
      const result = get(response, "results", []);
      setSearchResult(
        result
          .filter(
            (article: IZendeskArticle) =>
              !isHiddenSection(article.section_id) && !article.draft
          )
          .map((article: IZendeskArticle) => ({
            id: article.id?.toString() ?? "",
            title: article.title ?? "",
            snippet: article.snippet ?? "",
          }))
      );
      if (result.length) {
        toggleResult();
        // when we have search result, we track the search input
        trackHelpCenterSearchArticleKeyboardInput(
          isBrandSearch ? "brand" : "retailer",
          getRetailerToken() ?? "",
          getBrandToken() ?? "",
          searchValue,
          sectionFrom
        );
      }
      setLoading(false);
    } catch (err) {
      setSearchResult([]);
      toggleResult(true);
      setLoading(false);
    }
  };

  const onBlur = (event: React.FormEvent<HTMLElement>) => {
    const currentTarget = get(event, "currentTarget");
    setTimeout(() => {
      if (!currentTarget.contains(document.activeElement)) {
        toggleResult(true);
      }
    }, 0);
  };

  const isBrandSearch = React.useMemo(() => {
    return (
      history.location?.pathname === brands() ||
      categoryId === BRANDS_CATEGORY_ID
    );
  }, [categoryId, history.location?.pathname]);

  const placeholder = React.useMemo(() => {
    return isBrandSearch
      ? strictLocalize({
          defaultMessage: "Search brand guides",
          description: {
            text: "Placeholder for the search bar",
          },
        })
      : strictLocalize({
          defaultMessage: "Search retailer guides",
          description: {
            text: "Placeholder for the search bar",
          },
        });
  }, [isBrandSearch, strictLocalize]);

  const initSearchState = (value: string) => {
    setSearchValue(value);
    setSelected(-1);
  };

  const search = () => {
    const selectedArticle = searchResult[selected];
    if (selectedArticle) {
      history.push(articlePage(selectedArticle.id));
      resetSearch();
    }
    // TODO: use appHistory.history.push(`${search}?query=${this.searchValue}`); when we get a search page design
  };

  const navigateResult = (event: React.KeyboardEvent<HTMLElement>) => {
    const keyCode = event.key || event.keyCode;
    if (
      (keyCode === 40 || keyCode === "ArrowDown") &&
      selected < searchResult.length - 1
    ) {
      setSelected(selected + 1);
    } else if ((keyCode === 38 || keyCode === "ArrowUp") && selected > 0) {
      setSelected(selected - 1);
    } else if ((keyCode === 13 || keyCode === "Enter") && searchValue) {
      if (selected !== -1) {
        search();
      } else if (history.location?.pathname !== home()) {
        trackHelpCenterSearchEnterKeyboardInput(
          history.location?.pathname ?? ""
        );
        history.push(
          `${searchPage()}?${qs.stringify({
            categoryId: categoryId,
            searchTerm: searchValue,
          })}`
        );
      }
    }
  };

  const resetSearch = () => {
    toggleResult(true);
    setSearchValue("");
    setSelected(-1);
  };

  const toggleResult = (hide?: boolean) => {
    setShowResult(!hide);
  };

  React.useEffect(() => {
    if (itemRef.current && listRef.current) {
      scrollToTopIfNeeded(itemRef.current, listRef.current);
    }
  }, [itemRef, listRef]);

  React.useEffect(() => {
    if (showResult) {
      trackHelpCenterSearchArticleKeyboardInputTraceRendered();
    }
  }, [showResult]);

  return (
    <Wrapper
      onBlur={(event) => onBlur(event)}
      $mini={mini}
      $miniInputSize={miniInputSize}
      $expanded={expanded}
    >
      {isLoading || searchIsLoading ? (
        <StyledLoadingSpinner size="small" />
      ) : null}
      <IconWrapper $mini={mini}>
        <Search color={Core.icon.subdued} />
      </IconWrapper>
      <SearchInput
        data-test-id="search-input"
        type="text"
        autoFocus={autoFocus}
        $placeholder={isLoading ? "" : placeholder}
        value={searchValue}
        onChange={(event) => fetchSearchResults(event)}
        onFocus={() => toggleResult(false)}
        onKeyDown={(event) => navigateResult(event)}
        className="search-help-center"
        $mini={mini}
      />
      {showResult && !!searchResult.length && (
        <SearchResult ref={listRef} data-test-id="search-result">
          {searchResult.map((article, index) => (
            <li
              key={article.id}
              ref={(ref) => {
                if (ref && selected === index) {
                  itemRef.current = ref;
                }
              }}
            >
              <Link
                to={{
                  pathname: articlePage(article.id),
                  state: {
                    navigatedFrom,
                    articleTitle: article.title,
                    pageFrom: history.location?.pathname,
                    sectionFrom,
                  },
                }}
                onClick={() => {
                  toggleResult(true);
                  resetSearch();
                }}
                className={selected === index ? "search-item-selected" : ""}
                onFocus={() => setSelected(index)}
              >
                <h2>{article.title}</h2>
                <Spacer height="1x" />
                <SafelySetInnerHTML
                  html={
                    mini
                      ? `${article.snippet.slice(0, 70)}...`
                      : article.snippet
                  }
                  tag="p"
                />
              </Link>
            </li>
          ))}
        </SearchResult>
      )}
    </Wrapper>
  );
};
